import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "fileInput",
    "dropZone",
    "dropZoneIdle",
    "dropZoneUploading",
    "dropZoneReading",
    "fileName",
    "readingFileName",
    "progressBar",
    "progressText",
    "blobId",
    "errorMessage"
  ]

  static values = {
    directUploadUrl: String
  }

  connect() {
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.addEventListener("dragover", this.boundDragOver)
      this.dropZoneTarget.addEventListener("dragleave", this.boundDragLeave)
      this.dropZoneTarget.addEventListener("drop", this.boundDrop)
    }
  }

  disconnect() {
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.removeEventListener("dragover", this.boundDragOver)
      this.dropZoneTarget.removeEventListener("dragleave", this.boundDragLeave)
      this.dropZoneTarget.removeEventListener("drop", this.boundDrop)
    }
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")

    const file = event.dataTransfer.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  handleFileSelected(event) {
    const file = event.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  processFile(file) {
    // Validate CSV
    if (!file.name.toLowerCase().endsWith(".csv")) {
      this.showError("Please select a CSV file.")
      this.showIdle()
      return
    }

    // Validate size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      this.showError("File is too large. Maximum size is 10MB.")
      this.showIdle()
      return
    }

    this.clearError()
    this.showUploading(file.name)

    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.progressBarTarget.style.width = `${percent}%`
            this.progressTextTarget.textContent = `${percent}%`
          }
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        this.showIdle()
        return
      }

      this.blobIdTarget.value = blob.signed_id
      this.showReading(file.name)
      // The controller sits on the form-container that WRAPS the form, so
      // the form is a descendant, not an ancestor (closest() found nothing
      // and the upload sat on "Reading your file…" forever).
      const form = this.element.querySelector("form") || this.element.closest("form")
      form.requestSubmit()
    })
  }

  // ── Dropzone states (TASK-254) — one outer element, three inner blocks
  // the states below toggle; the upload completing submits the form itself
  // (no "Upload & Continue" button), and an error returns the dropzone to
  // idle.

  showIdle() {
    this.dropZoneTarget.classList.remove("border-solid", "border-blue-600", "bg-blue-50")
    this.dropZoneTarget.classList.add("border-dashed", "border-gray-300")
    this.dropZoneIdleTarget.classList.remove("hidden")
    this.dropZoneUploadingTarget.classList.add("hidden")
    this.dropZoneReadingTarget.classList.add("hidden")
    this.progressBarTarget.style.width = "0%"
    this.progressTextTarget.textContent = "0%"
  }

  showUploading(fileName) {
    this.dropZoneTarget.classList.remove("border-dashed", "border-gray-300")
    this.dropZoneTarget.classList.add("border-solid", "border-blue-600", "bg-blue-50")
    this.fileNameTarget.textContent = fileName
    this.progressBarTarget.style.width = "0%"
    this.progressTextTarget.textContent = "0%"
    this.dropZoneIdleTarget.classList.add("hidden")
    this.dropZoneUploadingTarget.classList.remove("hidden")
    this.dropZoneReadingTarget.classList.add("hidden")
  }

  showReading(fileName) {
    this.readingFileNameTarget.textContent = fileName
    this.dropZoneIdleTarget.classList.add("hidden")
    this.dropZoneUploadingTarget.classList.add("hidden")
    this.dropZoneReadingTarget.classList.remove("hidden")
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }
}
